/* eslint-disable eqeqeq */
import React, { useState } from 'react';
import { useDataProvider } from 'react-admin';
import LoadingButton from '../../../components/controls/LoadingButton';
import { Box, Button, Grid, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { formatLocalTime } from '../../../components/ex_dayjs';
import MDialog from '../../../components/controls/MDialog';
import { Check, Clear, Create, Settings } from '@mui/icons-material';
import RulePopup from './RulePopup';
import MSelect from '../../../components/controls/MSelect';
import type { IData } from '../../../types';

interface SearchFieldProps {
    label: string;
    onClear: () => void;
    onFilter: (value: string) => void;
    disabled?: boolean;
    [key: string]: any;
}

const SearchField = ({ label, onClear, onFilter, disabled, ...props }: SearchFieldProps) => {
    const [value, setValue] = useState<string>();

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        onFilter(newValue);
    };

    const handleClear = () => {
        onClear();
        setValue('');
    };

    return (
        <TextField
            {...props}
            id={`filter-id-${label}`}
            label={label}
            variant="outlined"
            onChange={onChange}
            value={value}
            disabled={disabled}
            slotProps={{
                input: {
                    endAdornment: value ? (
                        <InputAdornment position="end">
                            <IconButton onClick={handleClear} edge="end">
                                <Clear style={{ fontSize: 'large' }} />
                            </IconButton>
                        </InputAdornment>
                    ) : null
                }
            }}
        />
    );
};

const ConfigButton = () => {
    const dataProvider = useDataProvider();
    const [isOpen, setOpen] = useState(false);
    const [data, setData] = useState<IData[]>([]);
    const [rule, setRule] = useState<{ isOpenRule: boolean; rowSelected: IData | null }>({
        isOpenRule: false,
        rowSelected: null
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'updatedOn', sort: 'desc' }]);
    //#region const for filters
    const [filteredRows, setFilteredRows] = useState<IData[]>([]);
    const [filter, setFilter] = useState<IData>({
        description: '',
        mailbox: '',
        subject: '',
        forwardTo: '',
        ruleType: ''
    });
    //#endregion

    const RuleTypeList = [
        { id: 0, name: 'System' },
        { id: 1, name: 'User' }
    ];

    async function getData () {
        let response = await dataProvider.fetchData('Email', 'GetEmailRules');
        if (response && response.data.items) {
            setOpen(true);
            setData(response.data.items);
            setFilteredRows(response.data.items);
        }
    }

    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onRowClick = (params: IData) => {
        setRule((prev) => ({ ...prev, isOpenRule: true, rowSelected: params.row }));
        setOpen(false);
    };

    const onCreate = () => {
        setRule((prev) => ({ ...prev, isOpenRule: true, rowSelected: { active: false, priority: 100, ruleType: 1 } }));
        setOpen(false);
    };

    const onResetRule = () => {
        setOpen(true);
        setRule((prev) => ({ ...prev, isOpenRule: false, rowSelected: null }));
    };

    //#region Filters

    const updateFilter = (name: string, value: string) => {
        setFilter((prev: IData) => ({ ...prev, [name]: value }));
    };

    const onFilter = () => {
        let newSource = data;
        for (let key of Object.keys(filter)) {
            if (filter[key] || typeof (filter[key]) === 'number') {
                newSource = newSource.filter(e => e[key] != null && String(e[key]).toLowerCase().includes(String(filter[key]).toLowerCase()));
            }
        }
        setFilteredRows(newSource);
    };

    React.useEffect(() => {
        onFilter();
    }, [filter]);
    //#endregion

    return (
        <>
            <LoadingButton
                size="small"
                variant="text"
                onClick={getData}
                startIcon={<Settings />}
            >
                Config Rules
            </LoadingButton>
            {isOpen &&
                <MDialog
                    name={'dlg-email-rule-list'}
                    open={isOpen}
                    title={'Email Rules'}
                    handleClose={onClose}
                    maxWidth="xl"
                    customHeader={
                        <Stack width='100%' direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" style={{ marginTop: 10 }}>
                            <span>Email Rules</span>
                            <Button startIcon={<Create />} variant="outlined" color='info' onClick={onCreate}>Create</Button>
                        </Stack>
                    }
                >
                    <Grid container spacing={2} sx={{ marginBottom: '5px' }}>
                        <Grid item size={2.5}>
                            <SearchField
                                label={'Description'}
                                name={'description'}
                                disabled={data.length == 0}
                                onClear={() => updateFilter('description', '')}
                                onFilter={(value: string) => updateFilter('description', value)}
                            />
                        </Grid>
                        <Grid item size={2.5}>
                            <SearchField
                                label={'Mail box'}
                                name={'mailBox'}
                                disabled={data.length == 0}
                                onClear={() => updateFilter('mailBox', '')}
                                onFilter={(value: string) => updateFilter('mailBox', value)}
                            />
                        </Grid>
                        <Grid item size={2.5}>
                            <SearchField
                                label={'Subject'}
                                name={'subject'}
                                disabled={data.length == 0}
                                onClear={() => updateFilter('subject', '')}
                                onFilter={(value: string) => updateFilter('subject', value)}
                            />
                        </Grid>
                        <Grid item size={2.5}>
                            <SearchField
                                label={'ForwardTo'}
                                name={'forwardTo'}
                                disabled={data.length == 0}
                                onClear={() => updateFilter('forwardTo', '')}
                                onFilter={(value: string) => updateFilter('forwardTo', value)}
                            />
                        </Grid>
                        <Grid item size={1.5}>
                            <MSelect
                                label={'Rule Type'}
                                variant="outlined"
                                choices={RuleTypeList}
                                onChange={(value: string) => updateFilter('ruleType', value)} />
                        </Grid>
                    </Grid>
                    <Box sx={{ maxHeight: 800, height: 600, width: '100%' }}>
                        <DataGrid
                            sortModel={sortModel}
                            rows={filteredRows}
                            columns={[
                                { field: 'id', headerName: 'Id', flex: 0.5 },
                                { field: 'description', headerName: 'Description', flex: 1 },
                                { field: 'mailBox', headerName: 'Mail Box', flex: 1 },
                                { field: 'subject', headerName: 'Subject', flex: 1 },
                                { field: 'forwardTo', headerName: 'Forward To', flex: 2 },
                                { field: 'updatedOn', headerName: 'Updated On', flex: 1, valueFormatter: (value) => formatLocalTime(value) },
                                {
                                    field: 'active', headerName: 'Active', align: 'center', headerAlign: 'center', flex: 0.5, renderCell: (params) => (
                                        params.value ? <Check color="success" /> : <Clear color="error" />
                                    ),
                                }
                            ]}
                            onSortModelChange={handleSortModelChange}
                            onRowClick={onRowClick}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 25,
                                    }
                                },
                            }}
                        />
                    </Box>
                </MDialog>
            }
            {rule.isOpenRule &&
                <RulePopup dataProvider={dataProvider} data={rule} ruleTypeList={RuleTypeList}
                    onClose={onResetRule}
                    reloadPage={() => {
                        //Reset data
                        onResetRule();
                        setData([]);
                        getData();
                    }} />}
        </>
    );
};

export default React.memo(ConfigButton);
